import React from "react";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import BuildIcon from "@mui/icons-material/Build";

/**
 * Unified Tool Passport Modal Component
 *
 * @param {boolean} open - Controls modal visibility
 * @param {function} onClose - Handler for closing the modal
 * @param {object} toolPassport - The tool passport data to display
 */
const ToolPassportModal = ({ open, onClose, toolPassport }) => {
  const { t } = useTranslation();

  if (!toolPassport) return null;

  const cellStyle = {
    verticalAlign: 'top',
    wordWrap: 'break-word',
    border: '1px solid #e2e8f0',
    padding: '12px 16px',
    fontSize: '0.9em',
    lineHeight: '1.5'
  };

  const labelCellStyle = {
    ...cellStyle,
    width: '35%',
    background: '#f8fafc',
    fontWeight: '600',
    color: '#374151'
  };

  const valueCellStyle = {
    ...cellStyle,
    color: '#1e293b'
  };

  const renderRow = (labelKey, value, isHtml = false) => {
    if (!value && value !== 0) return null;

    return (
      <tr>
        <td style={labelCellStyle}>{t(labelKey)}</td>
        <td style={valueCellStyle}>
          {isHtml ? (
            <span dangerouslySetInnerHTML={{ __html: value }} />
          ) : (
            value
          )}
        </td>
      </tr>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: '#fff',
          padding: '16px 24px'
        }}
      >
        <BuildIcon sx={{ fontSize: 24 }} />
        <span style={{ fontWeight: '600', fontSize: '1.1em' }}>
          {t("toolPassport")}
        </span>
        <IconButton
          onClick={onClose}
          sx={{
            ml: 'auto',
            color: '#fff',
            '&:hover': { background: 'rgba(255,255,255,0.1)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ padding: '24px' }}>
        {/* Tool Header with Logo */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          marginBottom: '24px',
          padding: '16px',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: '10px',
          border: '1px solid #e2e8f0'
        }}>
          <img
            src={toolPassport.logo
              ? `${process.env.REACT_APP_SERVER_URLForLogo}/${toolPassport.logo}`
              : `/assets/brand/default_img.png`}
            alt="Tool Logo"
            style={{
              width: '80px',
              height: '80px',
              objectFit: 'contain',
              borderRadius: '8px',
              background: '#fff',
              padding: '8px',
              border: '1px solid #e2e8f0'
            }}
            onError={(e) => { e.target.onerror = null; e.target.src = '/assets/brand/default_img.png'; }}
          />
          <div style={{ flex: 1 }}>
            <h3 style={{
              margin: '0 0 8px 0',
              color: '#1e293b',
              fontSize: '1.3em',
              fontWeight: '700'
            }}>
              {toolPassport.name}
            </h3>
            <div style={{
              color: '#64748b',
              fontSize: '0.9em',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              <span style={{
                background: '#e0e7ff',
                color: '#4338ca',
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '0.85em',
                fontWeight: '500'
              }}>
                {toolPassport.typeOfToolName}
              </span>
              <span style={{ color: '#94a3b8' }}>•</span>
              <span>{toolPassport.panelName} / {toolPassport.categoryName}</span>
            </div>
            {toolPassport.toolURL && (
              <a
                href={toolPassport.toolURL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginTop: '10px',
                  color: '#2563eb',
                  textDecoration: 'none',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  padding: '6px 12px',
                  background: '#eff6ff',
                  borderRadius: '6px',
                  border: '1px solid #bfdbfe',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#dbeafe';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#eff6ff';
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 16 }} />
                {t("visitWebsite")}
              </a>
            )}
          </div>
        </div>

        {/* Details Table */}
        <table style={{
          width: '100%',
          tableLayout: 'fixed',
          borderCollapse: 'collapse',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid #e2e8f0'
        }}>
          <tbody>
            {renderRow("toolPassportApplicationArea", toolPassport.subcategoryName)}
            {renderRow("toolPassportFunctions", toolPassport.function, true)}
            {renderRow("toolPassportAbstractionLevel", toolPassport.abstractionLevel, true)}
            {renderRow("toolPassportIOConnection", toolPassport.ioConnection, true)}
            {renderRow("toolPassportVerificationTool", toolPassport.verificationTool, true)}
            {renderRow("toolPassportApplicationAreaOrEquivalenceCoverage", toolPassport.applicationAreaOrEquivalenceCoverage, true)}
            {renderRow("toolPassportQualitativeFeature", toolPassport.qualitativeFeature, true)}
            {renderRow("toolPassportQuantitativeFeature", toolPassport.quantitativeFeature, true)}
          </tbody>
        </table>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{
        padding: '16px 24px',
        background: '#f8fafc',
        borderTop: '1px solid #e2e8f0'
      }}>
        <Button
          onClick={onClose}
          sx={{
            padding: '8px 24px',
            background: '#f1f5f9',
            color: '#475569',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: '500',
            '&:hover': {
              background: '#e2e8f0'
            }
          }}
        >
          {t("Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ToolPassportModal;
